// Admin → Roles (#516): what Co-parent, Tutor and Viewer can do, and new roles. Every family's "Can do" and invite form
// offer these; a family owner can still give one person Custom levels.
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { AccessLevel, PermissionCatalog } from '../../lib/types';
import { useParent } from '../ParentContext';
import { Collapsible } from '../../ui/Collapsible';

interface Role {
  id: string;
  label: string;
  hint: string;
  levels: Record<string, AccessLevel>;
  builtIn: boolean;
  members: number;
}

export function Roles() {
  const { toast } = useParent();
  const [data, setData] = useState<{ roles: Role[]; catalog: PermissionCatalog } | null>(null);
  const [adding, setAdding] = useState(false);
  // Find a role by name or description (#523), for when there are many.
  const [query, setQuery] = useState('');
  const load = useCallback(() => {
    api
      .get<{ roles: Role[]; catalog: PermissionCatalog }>('/api/admin/roles')
      .then(setData)
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);
  if (!data) return <p className="muted">Loading…</p>;

  const blank: Role = { id: '', label: '', hint: '', levels: Object.fromEntries(data.catalog.areas.map((a) => [a.key, 'none'])), builtIn: false, members: 0 };
  return (
    <div className="stack">
      <p className="muted small">
        A role is what an adult in a family can do in each area. Every family’s “Can do” and invite form offer these roles; an owner can still give one
        person Custom levels. Co-parent, Tutor and Viewer can be changed but not deleted. Deleting a role you added switches its members to Custom with
        the same access.
      </p>
      <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search roles by name or description" aria-label="Search roles" />
      {(() => {
        const q = query.trim().toLowerCase();
        const shown = q ? data.roles.filter((r) => r.label.toLowerCase().includes(q) || r.hint.toLowerCase().includes(q)) : data.roles;
        if (!shown.length) return <p className="muted">No roles match “{query.trim()}”.</p>;
        // Each starts closed (#523); open one to change it.
        return shown.map((r) => (
          <Collapsible
            key={r.id}
            id={`role-${r.id}`}
            title={
              <>
                {r.label}
                <span className="muted small role-meta">
                  {r.builtIn ? 'Built in · ' : ''}
                  {r.members} member{r.members === 1 ? '' : 's'}
                </span>
              </>
            }
          >
            <RoleCard role={r} catalog={data.catalog} onSaved={load} />
          </Collapsible>
        ));
      })()}
      {adding ? (
        <RoleCard
          role={blank}
          catalog={data.catalog}
          onSaved={() => {
            setAdding(false);
            load();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <div>
          <button className="btn primary" onClick={() => setAdding(true)}>
            <Plus size={16} /> New role
          </button>
        </div>
      )}
    </div>
  );
}

/** One role: its name, description and a level for each area. A blank id is a new role. */
function RoleCard({ role, catalog, onSaved, onCancel }: { role: Role; catalog: PermissionCatalog; onSaved: () => void; onCancel?: () => void }) {
  const { toast } = useParent();
  const [label, setLabel] = useState(role.label);
  const [hint, setHint] = useState(role.hint);
  const [levels, setLevels] = useState(role.levels);
  const [busy, setBusy] = useState(false);
  const isNew = !role.id;
  const dirty = isNew || label !== role.label || hint !== role.hint || catalog.areas.some((a) => levels[a.key] !== role.levels[a.key]);

  async function save() {
    setBusy(true);
    try {
      const body = { label: label.trim(), hint: hint.trim(), levels };
      if (isNew) await api.post('/api/admin/roles', body);
      else await api.put(`/api/admin/roles/${role.id}`, body);
      toast({ text: isNew ? `Added the role ${body.label}.` : `Saved ${body.label}.`, tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    const who = role.members ? ` Its ${role.members} member${role.members === 1 ? '' : 's'} switch to Custom with the same access.` : '';
    if (!confirm(`Delete the role ${role.label}?${who}`)) return;
    try {
      await api.del(`/api/admin/roles/${role.id}`);
      toast({ text: `Deleted ${role.label}.`, tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  // A new role is its own card; an existing one sits inside its (collapsible) section, which shows its name.
  const Wrap = isNew ? 'section' : 'div';
  return (
    <Wrap className={isNew ? 'card stack role-card' : 'stack role-card'}>
      {isNew && <strong>New role</strong>}
      <div className="row-gap wrap role-names">
        <label className="grow">
          Name
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} />
        </label>
        <label className="grow">
          Description
          <input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={120} placeholder="What they’re for" />
        </label>
      </div>
      <table className="permissions-grid">
        <tbody>
          {catalog.areas.map((a) => (
            <tr key={a.key}>
              <td>
                {a.label}
                <span className="muted small block">{a.hint}</span>
              </td>
              <td>
                <select value={levels[a.key] ?? 'none'} onChange={(e) => setLevels({ ...levels, [a.key]: e.target.value as AccessLevel })} aria-label={`${label || 'New role'}: ${a.label}`}>
                  {catalog.levels.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row-gap wrap">
        <button className="btn primary" onClick={save} disabled={busy || !dirty || !label.trim()}>
          {isNew ? 'Add role' : 'Save'}
        </button>
        {onCancel && (
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
        )}
        {!isNew && !role.builtIn && (
          <button className="btn danger-text" onClick={remove}>
            <Trash2 size={15} /> Delete role
          </button>
        )}
      </div>
    </Wrap>
  );
}
